import React, { useState } from 'react'
import { useDispatch } from 'react-redux'

import { FolderORM } from 'src/types/types'
import { folderActions } from 'src/state/redux/slice/folder'
import { DNAModalActions } from 'src/state/redux/slice/DNAModal/DNAModal'

import {
  TextField,
  DNAButton,
  DNAModal,
  DNAText,
  DNABox,
  GenericToast,
  ToastOrientations,
  useDisableSwipe,
} from '@alucio/lux-ui'
import { DNAModalVariant } from 'src/components/DNA/Modal/DNAConnectedModal'
import { ToastActions } from '@alucio/lux-ui/src/components/Toast/useToast'
import { useAppSettings } from 'src/state/context/AppSettings'

export enum FOLDER_OP {
  CREATE = 'CREATE',
  CREATE_SUBFOLDER = 'CREATE_SUBFOLDER',
  RENAME = 'RENAME',
}

export type FolderOp = {
  [K in keyof typeof FOLDER_OP] : {
    header: string,
    confirm: string
  }
}

const folderOp: FolderOp = {
  CREATE: {
    header: 'Create new folder',
    confirm: 'Create folder',
  },
  CREATE_SUBFOLDER: {
    header: 'Create new sub folder',
    confirm: 'Create sub folder',
  },
  RENAME: {
    header: 'Rename folder',
    confirm: 'Rename folder',
  },
}

export type DNAFolderUpsertModalProps = {
  folderORM?: FolderORM,
  mode?: FOLDER_OP | keyof typeof FOLDER_OP
  toast?: ToastActions
}

const Label = () => (
  <DNABox spacing="tiny" style={{ marginBottom: 4 }}>
    <DNAText b2 status="danger">*</DNAText>
    <DNAText b2>NAME</DNAText>
  </DNABox>
)

const DNAFolderUpsertModal: DNAModalVariant<DNAFolderUpsertModalProps> = (props) => {
  useDisableSwipe()
  const {
    toggleModal,
    folderORM,
    mode = folderORM ? FOLDER_OP.RENAME : FOLDER_OP.CREATE,
    toast,
  } = props
  const dispatch = useDispatch()
  const [input, setInput] = useState<string>(
    mode === FOLDER_OP.RENAME && folderORM
      ? folderORM.model.name
      : '',
  )

  const { deviceMode } = useAppSettings()
  const isTablet = deviceMode === 'tablet';
  const headerCopy = folderOp[mode].header
  const confirmCopy = folderOp[mode].confirm

  const onAction = () => {
    if (mode === FOLDER_OP.RENAME && folderORM) {
      analytics?.track('FOLDER_RENAME', {
        action: 'RENAME',
        category: 'FOLDER',
        folderId: folderORM.model.id,
      });

      dispatch(folderActions.rename(input, folderORM.model))
      toast?.add(
        <GenericToast
          title="Folder renamed."
          status="success"
        />,
        ToastOrientations.TOP_RIGHT,
      )
    }
    if (mode === FOLDER_OP.CREATE_SUBFOLDER && folderORM) {
      dispatch(folderActions.createSubFolder(folderORM, input))
      toast?.add(
        <GenericToast
          title="Folder created."
          status="success"
        />,
        ToastOrientations.TOP_RIGHT,
      )
    }

    if (mode === FOLDER_OP.CREATE) {
      dispatch(folderActions.create({ name: input }))
      toast?.add(
        <GenericToast
          title="Folder created."
          status="success"
        />,
        ToastOrientations.TOP_RIGHT,
      )
    }

    dispatch(DNAModalActions.toggleModal())
  }
  const canSave = input.trim().length > 0;
  return (
    <DNAModal>
      <DNAModal.Header onClose={toggleModal}>
        { headerCopy }
      </DNAModal.Header>
      <DNAModal.Body>
        <TextField.Kitten
          style={{ minWidth: 567 }}
          autoFocus={!isTablet}
          onKeyPress={ (event) => {
            if (!canSave && event.nativeEvent.key === 'Enter') {
              event.preventDefault();
              return;
            }
            event.nativeEvent.key === 'Enter' && onAction()
          }}
          label={Label}
          value={input}
          onChangeText={setInput}
        />
      </DNAModal.Body>
      <DNAModal.Confirmation>
        <DNAButton onPress={toggleModal} status="subtle">
          Cancel
        </DNAButton>
        <DNAButton disabled={!canSave} onPress={onAction} status="primary">
          { confirmCopy }
        </DNAButton>
      </DNAModal.Confirmation>
    </DNAModal>
  )
}

export default DNAFolderUpsertModal
